/* eslint-disable react/jsx-no-bind, react/no-multi-comp */
import React, { useEffect, useMemo, useState } from 'react';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { SCHEDULE_DEFAULT_DURATION_MINUTES, SCHEDULE_MUTE, SCHEDULE_WHEEL_HOURS, SCHEDULE_WHEEL_MINUTES, addMinutesIso, alignWheelMinutes, buildDefaultScheduleTitle, buildFutureDateValues, combineDurationMinutes, formatDurationLabel, formatMobileStartLabel, formatWheelDateLabel, getDefaultScheduleStart, splitDurationMinutes, toDateInputValue, toScheduledIso, toStartAudioMuted, toTimeInputValue } from '../scheduleUtils';
import { SchedulePickerDrawer, WheelColumn } from './ScheduleWheelPicker';
function BackIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M15 6l-6 6 6 6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
function ChevronIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M9 6l6 6-6 6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
/**
 * 预定表单开关行。
 *
 * @param {Object} props - Props.
 * @returns {React.ReactElement}
 */
function ScheduleSwitchRow({ checked, label, onToggle }) {
    return (React.createElement("div", { className: 'portal-schedule-switch-row' },
        React.createElement("span", null, label),
        React.createElement("button", { "aria-checked": checked, className: checked ? 'portal-schedule-switch is-on' : 'portal-schedule-switch', onClick: onToggle, role: 'switch', type: 'button' })));
}
function pad2(value) {
    return String(value).padStart(2, '0');
}
function parseHourMinute(timeValue) {
    const [hour = '10', minute = '00'] = timeValue.split(':');
    return {
        hour: pad2(Number(hour) || 0),
        minute: pad2(alignWheelMinutes(Number(minute) || 0))
    };
}
/**
 * 手机 Web 预定会议：先选常规会议，再进整页表单；时间用底部滚轮抽屉。
 *
 * @param {IProps} props - Props.
 * @returns {React.ReactElement}
 */
export default function ScheduleMeetingMobile({ busy, displayName, error, onClose, onSubmit }) {
    const defaultStart = useMemo(() => getDefaultScheduleStart(), []);
    const todayValue = useMemo(() => toDateInputValue(new Date()), []);
    const dateOptions = useMemo(() => {
        const values = buildFutureDateValues();
        return values.map(value => ({
            label: value === todayValue ? M.SCHEDULE_TODAY : formatWheelDateLabel(value, todayValue),
            value
        }));
    }, [todayValue]);
    const hourOptions = useMemo(() => SCHEDULE_WHEEL_HOURS.map(hour => ({
        label: pad2(hour),
        value: pad2(hour)
    })), []);
    const minuteOptions = useMemo(() => SCHEDULE_WHEEL_MINUTES.map(minute => ({
        label: pad2(minute),
        value: pad2(minute)
    })), []);
    const hourDurationOptions = useMemo(() => SCHEDULE_WHEEL_HOURS.map(hour => ({
        label: pad2(hour),
        value: String(hour)
    })), []);
    const minuteDurationOptions = useMemo(() => SCHEDULE_WHEEL_MINUTES.map(minute => ({
        label: pad2(minute),
        value: String(minute)
    })), []);
    const [step, setStep] = useState('type');
    const [title, setTitle] = useState(() => buildDefaultScheduleTitle(displayName));
    const [dateValue, setDateValue] = useState(defaultStart.date);
    const [timeValue, setTimeValue] = useState(() => {
        const parsed = parseHourMinute(defaultStart.time);
        return `${parsed.hour}:${parsed.minute}`;
    });
    const [durationMinutes, setDurationMinutes] = useState(SCHEDULE_DEFAULT_DURATION_MINUTES);
    const [muteMode, setMuteMode] = useState(SCHEDULE_MUTE.OFF);
    const [autoCloudRecording, setAutoCloudRecording] = useState(false);
    const [calendarOn, setCalendarOn] = useState(true);
    const [waitingRoomOn, setWaitingRoomOn] = useState(false);
    const [passwordOn, setPasswordOn] = useState(false);
    const [joinPassword, setJoinPassword] = useState('');
    const [registrationOn, setRegistrationOn] = useState(false);
    const [joinBeforeHostOn, setJoinBeforeHostOn] = useState(true);
    const [multiDeviceOn, setMultiDeviceOn] = useState(true);
    const [blockAddContactOn, setBlockAddContactOn] = useState(false);
    const [autoTranscriptOn, setAutoTranscriptOn] = useState(false);
    const [allowUploadDocOn, setAllowUploadDocOn] = useState(true);
    const [moreOpen, setMoreOpen] = useState(false);
    const [localError, setLocalError] = useState('');
    const [picker, setPicker] = useState(null);
    const [draftMuteMode, setDraftMuteMode] = useState(SCHEDULE_MUTE.OFF);
    const [draftDate, setDraftDate] = useState(defaultStart.date);
    const [draftHour, setDraftHour] = useState(parseHourMinute(defaultStart.time).hour);
    const [draftMinute, setDraftMinute] = useState(parseHourMinute(defaultStart.time).minute);
    const [draftDurationHours, setDraftDurationHours] = useState(() => String(splitDurationMinutes(SCHEDULE_DEFAULT_DURATION_MINUTES).hours));
    const [draftDurationMinutes, setDraftDurationMinutes] = useState(() => String(alignWheelMinutes(splitDurationMinutes(SCHEDULE_DEFAULT_DURATION_MINUTES).minutes)));
    useEffect(() => {
        document.body.classList.add('portal-schedule-lock');
        logger.info(`[门户] ${new Date().toISOString()} 打开手机预定会议 | step=type`);
        return () => {
            document.body.classList.remove('portal-schedule-lock');
        };
    }, []);
    const openStartPicker = () => {
        const parsed = parseHourMinute(timeValue);
        setDraftDate(dateValue);
        setDraftHour(parsed.hour);
        setDraftMinute(parsed.minute);
        setPicker('start');
        logger.info(`[门户] ${new Date().toISOString()} 打开开始时间滚轮`
            + ` | date=${dateValue} time=${timeValue}`);
    };
    const openDurationPicker = () => {
        const split = splitDurationMinutes(durationMinutes);
        setDraftDurationHours(String(split.hours));
        setDraftDurationMinutes(String(alignWheelMinutes(split.minutes)));
        setPicker('duration');
        logger.info(`[门户] ${new Date().toISOString()} 打开会议时长滚轮 | durationMin=${durationMinutes}`);
    };
    const openEndPicker = () => {
        const endIso = addMinutesIso(toScheduledIso(dateValue, timeValue), durationMinutes);
        const endDate = new Date(endIso);
        const parsed = parseHourMinute(toTimeInputValue(endDate));
        setDraftDate(toDateInputValue(endDate));
        setDraftHour(parsed.hour);
        setDraftMinute(parsed.minute);
        setPicker('end');
        logger.info(`[门户] ${new Date().toISOString()} 切换选择结束时间`);
    };
    const confirmPicker = () => {
        if (picker === 'start') {
            const nextTime = `${draftHour}:${draftMinute}`;
            setDateValue(draftDate);
            setTimeValue(nextTime);
            logger.info(`[门户] ${new Date().toISOString()} 确认开始时间 | date=${draftDate} time=${nextTime}`);
        }
        else if (picker === 'duration') {
            const next = combineDurationMinutes(Number(draftDurationHours) || 0, Number(draftDurationMinutes) || 0);
            setDurationMinutes(next);
            logger.info(`[门户] ${new Date().toISOString()} 确认会议时长 | durationMin=${next}`);
        }
        else if (picker === 'end') {
            const startMs = new Date(toScheduledIso(dateValue, timeValue)).getTime();
            const endMs = new Date(toScheduledIso(draftDate, `${draftHour}:${draftMinute}`)).getTime();
            const diffMin = Math.round((endMs - startMs) / 60000);
            if (diffMin < 5) {
                setLocalError(M.SCHEDULE_REQUIRED);
                logger.warn(`[门户] ${new Date().toISOString()} 结束时间早于开始时间 | start=${dateValue} ${timeValue}`
                    + ` end=${draftDate} ${draftHour}:${draftMinute}`);
                setPicker(null);
                return;
            }
            const aligned = Math.max(5, Math.round(diffMin / 5) * 5);
            setDurationMinutes(aligned);
            logger.info(`[门户] ${new Date().toISOString()} 确认结束时间换算时长 | durationMin=${aligned}`);
        }
        else if (picker === 'mute') {
            setMuteMode(draftMuteMode);
            logger.info(`[门户] ${new Date().toISOString()} 确认入会静音 | mute=${draftMuteMode}`);
        }
        setPicker(null);
    };
    const muteStatusText = (mode) => {
        if (mode === SCHEDULE_MUTE.ON) {
            return M.SCHEDULE_MUTE_ON;
        }
        if (mode === SCHEDULE_MUTE.AFTER_6) {
            return M.SCHEDULE_MUTE_AFTER_6;
        }
        return M.SCHEDULE_MUTE_OFF;
    };
    const openMutePicker = () => {
        setDraftMuteMode(muteMode);
        setPicker('mute');
        logger.info(`[门户] ${new Date().toISOString()} 打开入会静音选项 | mute=${muteMode}`);
    };
    const goForm = () => {
        setStep('form');
        logger.info(`[门户] ${new Date().toISOString()} 预定会议进入表单 | type=regular`);
    };
    const goBack = () => {
        if (step === 'form') {
            setStep('type');
            setPicker(null);
            logger.info(`[门户] ${new Date().toISOString()} 预定会议返回类型选择`);
            return;
        }
        onClose();
    };
    const submitSchedule = () => {
        const trimmed = title.trim();
        if (!trimmed || !dateValue || !timeValue) {
            setLocalError(M.SCHEDULE_REQUIRED);
            logger.warn(`[门户] ${new Date().toISOString()} 预定会议校验失败 | 缺少主题或开始时间`);
            return;
        }
        setLocalError('');
        const scheduledStart = toScheduledIso(dateValue, timeValue);
        const scheduledEnd = addMinutesIso(scheduledStart, durationMinutes);
        const startAudioMuted = toStartAudioMuted(muteMode);
        logger.info(`[门户] ${new Date().toISOString()} 提交预定会议`
            + ` | title=${trimmed} start=${scheduledStart} durationMin=${durationMinutes}`
            + ` mute=${muteMode} autoRecord=${autoCloudRecording}`);
        onSubmit({
            autoCloudRecording,
            scheduledEnd,
            scheduledStart,
            startAudioMuted,
            title: trimmed
        });
        /*
        TODO(预定更多): 后端 ScheduleRequest 补齐后打开下列字段一并提交
        waitingRoom: waitingRoomOn,
        joinPassword: passwordOn ? joinPassword.trim() : undefined,
        registration: registrationOn,
        joinBeforeHost: joinBeforeHostOn,
        multiDevice: multiDeviceOn,
        blockAddContact: blockAddContactOn,
        autoTranscript: autoTranscriptOn,
        allowUploadDoc: allowUploadDocOn,
        calendar: calendarOn
        */
    };
    const onFormSubmit = (event) => {
        event.preventDefault();
        submitSchedule();
    };
    const pickerTitle = picker === 'duration'
        ? M.SCHEDULE_DURATION_LONG
        : picker === 'end'
            ? M.SCHEDULE_END_TIME
            : picker === 'mute'
                ? M.SCHEDULE_MUTE_ON_ENTRY
                : M.SCHEDULE_START;
    return (React.createElement("div", { className: 'portal-schedule-mobile' },
        React.createElement("header", { className: 'portal-schedule-mobile-header' },
            React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-schedule-mobile-icon', onClick: goBack, type: 'button' },
                React.createElement(BackIcon, null)),
            React.createElement("h2", null, step === 'type' ? M.SCHEDULE_TYPE_TITLE : M.SCHEDULE_MEETING),
            step === 'form' ? (React.createElement("button", { className: 'portal-schedule-mobile-done', disabled: busy, onClick: submitSchedule, type: 'button' }, M.SCHEDULE_DONE)) : (React.createElement("span", { className: 'portal-schedule-mobile-header-spacer' }))),
        step === 'type' ? (React.createElement("div", { className: 'portal-schedule-type-body' },
            React.createElement("button", { className: 'portal-schedule-type-card is-selected', type: 'button' },
                React.createElement("span", { className: 'portal-schedule-type-check', "aria-hidden": true },
                    React.createElement("svg", { fill: 'none', height: '10', viewBox: '0 0 12 12', width: '10' },
                        React.createElement("path", { d: 'M2 6.2 4.6 8.8 10 3.2', stroke: '#fff', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.8' }))),
                M.SCHEDULE_TYPE_REGULAR),
            React.createElement("div", { className: 'portal-schedule-type-footer' },
                React.createElement("button", { className: 'portal-schedule-next', onClick: goForm, type: 'button' }, M.SCHEDULE_NEXT)))) : (React.createElement("form", { className: 'portal-schedule-mobile-form', onSubmit: onFormSubmit },
            React.createElement("div", { className: 'portal-schedule-mobile-title' },
                React.createElement("input", { onChange: event => setTitle(event.target.value), value: title })),
            React.createElement("button", { className: 'portal-schedule-nav-row', onClick: openStartPicker, type: 'button' },
                React.createElement("span", null, M.SCHEDULE_START),
                React.createElement("span", { className: 'portal-schedule-nav-value' },
                    formatMobileStartLabel(dateValue, timeValue),
                    React.createElement(ChevronIcon, null))),
            React.createElement("button", { className: 'portal-schedule-nav-row', onClick: openDurationPicker, type: 'button' },
                React.createElement("span", null, M.SCHEDULE_DURATION_LONG),
                React.createElement("span", { className: 'portal-schedule-nav-value' },
                    formatDurationLabel(durationMinutes),
                    React.createElement(ChevronIcon, null))),
            React.createElement("div", { className: 'portal-schedule-info-box' }, M.SCHEDULE_HINT),
            React.createElement("div", { className: 'portal-schedule-nav-row is-static' },
                React.createElement("span", null, M.SCHEDULE_TIMEZONE),
                React.createElement("span", { className: 'portal-schedule-nav-value' },
                    M.SCHEDULE_TIMEZONE_BEIJING,
                    React.createElement(ChevronIcon, null))),
            React.createElement("div", { className: 'portal-schedule-nav-row is-static' },
                React.createElement("span", null, M.SCHEDULE_REPEAT),
                React.createElement("span", { className: 'portal-schedule-nav-value' },
                    M.SCHEDULE_REPEAT_NONE,
                    React.createElement(ChevronIcon, null))),
            React.createElement("div", { className: 'portal-schedule-nav-row is-static' },
                React.createElement("span", null, M.SCHEDULE_PARTICIPANTS),
                React.createElement("span", { className: 'portal-schedule-nav-value' },
                    M.SCHEDULE_ADD,
                    React.createElement(ChevronIcon, null))),
            React.createElement("div", { className: 'portal-schedule-switch-row' },
                React.createElement("span", null, M.SCHEDULE_CALENDAR),
                React.createElement("button", { "aria-checked": calendarOn, className: calendarOn ? 'portal-schedule-switch is-on' : 'portal-schedule-switch', onClick: () => {
                        setCalendarOn(on => !on);
                        // TODO(预定更多): 对接日历提醒（写入系统日历 / 门户提醒）
                        logger.info(`[门户] ${new Date().toISOString()} 切换日历 | on=${!calendarOn}`);
                    }, role: 'switch', type: 'button' })),
            React.createElement("button", { className: 'portal-schedule-more', onClick: () => {
                    setMoreOpen(open => !open);
                    logger.info(`[门户] ${new Date().toISOString()} ${moreOpen ? '收起' : '展开'}预定更多设置`);
                }, type: 'button' },
                M.SCHEDULE_MORE,
                React.createElement("span", { className: moreOpen ? 'is-open' : undefined }, "\u2304")),
            moreOpen ? (React.createElement("div", { className: 'portal-schedule-more-block' },
                React.createElement(ScheduleSwitchRow, { checked: waitingRoomOn, label: M.SCHEDULE_WAITING_ROOM, onToggle: () => {
                        setWaitingRoomOn(on => !on);
                        // TODO(预定更多): 对接会议等候室 lobby，预定落库 + 进会 config.lobby
                        logger.info(`[门户] ${new Date().toISOString()} 切换等候室 | on=${!waitingRoomOn}`);
                    } }),
                React.createElement(ScheduleSwitchRow, { checked: passwordOn, label: M.SCHEDULE_JOIN_PASSWORD, onToggle: () => {
                        setPasswordOn(on => !on);
                        // TODO(预定更多): 对接入会密码，预定落库 + 进会锁定房间
                        logger.info(`[门户] ${new Date().toISOString()} 切换入会密码 | on=${!passwordOn}`);
                    } }),
                passwordOn ? (React.createElement("div", { className: 'portal-schedule-password-wrap' },
                    React.createElement("input", { onChange: event => setJoinPassword(event.target.value), placeholder: M.SCHEDULE_PASSWORD_PLACEHOLDER, value: joinPassword }))) : null,
                React.createElement(ScheduleSwitchRow, { checked: registrationOn, label: M.SCHEDULE_REGISTRATION, onToggle: () => {
                        setRegistrationOn(on => !on);
                        // TODO(预定更多): 对接会议报名
                        logger.info(`[门户] ${new Date().toISOString()} 切换会议报名 | on=${!registrationOn}`);
                    } }),
                React.createElement(ScheduleSwitchRow, { checked: joinBeforeHostOn, label: M.SCHEDULE_JOIN_BEFORE_HOST, onToggle: () => {
                        setJoinBeforeHostOn(on => !on);
                        // TODO(预定更多): 对接允许成员在主持人前入会
                        logger.info(`[门户] ${new Date().toISOString()} 切换主持人前入会 | on=${!joinBeforeHostOn}`);
                    } }),
                React.createElement("button", { className: 'portal-schedule-nav-row', onClick: openMutePicker, type: 'button' },
                    React.createElement("span", null, M.SCHEDULE_MUTE_ON_ENTRY),
                    React.createElement("span", { className: 'portal-schedule-nav-value' },
                        muteStatusText(muteMode),
                        React.createElement(ChevronIcon, null))),
                React.createElement("button", { className: 'portal-schedule-nav-row', onClick: () => {
                        // TODO(预定更多): 打开会议水印设置并写入预定
                        logger.info(`[门户] ${new Date().toISOString()} TODO 会议水印尚未对接`);
                    }, type: 'button' },
                    React.createElement("span", null, M.SCHEDULE_WATERMARK),
                    React.createElement("span", { className: 'portal-schedule-nav-value' },
                        M.SCHEDULE_WATERMARK_OFF,
                        React.createElement(ChevronIcon, null))),
                React.createElement(ScheduleSwitchRow, { checked: multiDeviceOn, label: M.SCHEDULE_MULTI_DEVICE, onToggle: () => {
                        setMultiDeviceOn(on => !on);
                        // TODO(预定更多): 对接会议级多端入会（勿改账号偏好）
                        logger.info(`[门户] ${new Date().toISOString()} 切换多端入会 | on=${!multiDeviceOn}`);
                    } }),
                React.createElement("div", { className: 'portal-schedule-more-gap' }),
                React.createElement(ScheduleSwitchRow, { checked: blockAddContactOn, label: M.SCHEDULE_BLOCK_ADD_CONTACT, onToggle: () => {
                        setBlockAddContactOn(on => !on);
                        // TODO(预定更多): 对接禁止互相添加联系人
                        logger.info(`[门户] ${new Date().toISOString()} 切换禁止加联系人 | on=${!blockAddContactOn}`);
                    } }),
                React.createElement(ScheduleSwitchRow, { checked: autoCloudRecording, label: M.SCHEDULE_AUTO_RECORD, onToggle: () => {
                        setAutoCloudRecording(on => !on);
                        logger.info(`[门户] ${new Date().toISOString()} 切换自动云录制 | on=${!autoCloudRecording}`);
                    } }),
                React.createElement(ScheduleSwitchRow, { checked: autoTranscriptOn, label: M.SCHEDULE_AUTO_TRANSCRIPT, onToggle: () => {
                        setAutoTranscriptOn(on => !on);
                        // TODO(预定更多): 对接自动文字转写
                        logger.info(`[门户] ${new Date().toISOString()} 切换自动转写 | on=${!autoTranscriptOn}`);
                    } }),
                React.createElement("button", { className: 'portal-schedule-nav-row', onClick: () => {
                        // TODO(预定更多): 打开会议文档添加
                        logger.info(`[门户] ${new Date().toISOString()} TODO 会议文档尚未对接`);
                    }, type: 'button' },
                    React.createElement("span", null, M.SCHEDULE_DOCUMENTS),
                    React.createElement("span", { className: 'portal-schedule-nav-value' },
                        M.SCHEDULE_ADD,
                        React.createElement(ChevronIcon, null))),
                React.createElement(ScheduleSwitchRow, { checked: allowUploadDocOn, label: M.SCHEDULE_ALLOW_UPLOAD_DOC, onToggle: () => {
                        setAllowUploadDocOn(on => !on);
                        // TODO(预定更多): 对接允许成员上传文档
                        logger.info(`[门户] ${new Date().toISOString()} 切换允许上传文档 | on=${!allowUploadDocOn}`);
                    } }),
                React.createElement("button", { className: 'portal-schedule-nav-row', onClick: () => {
                        // TODO(预定更多): 打开投票添加
                        logger.info(`[门户] ${new Date().toISOString()} TODO 投票尚未对接`);
                    }, type: 'button' },
                    React.createElement("span", null, M.SCHEDULE_POLL),
                    React.createElement("span", { className: 'portal-schedule-nav-value' },
                        M.SCHEDULE_ADD,
                        React.createElement(ChevronIcon, null))),
                React.createElement("button", { className: 'portal-schedule-nav-row', onClick: () => {
                        // TODO(预定更多): 打开会议应用添加
                        logger.info(`[门户] ${new Date().toISOString()} TODO 会议应用尚未对接`);
                    }, type: 'button' },
                    React.createElement("span", null, M.SCHEDULE_APPS),
                    React.createElement("span", { className: 'portal-schedule-nav-value' },
                        M.SCHEDULE_ADD,
                        React.createElement(ChevronIcon, null))),
                React.createElement("div", { className: 'portal-schedule-more-gap' }),
                React.createElement("button", { className: 'portal-schedule-nav-row', onClick: () => {
                        // TODO(预定更多): 打开统一虚拟背景设置
                        logger.info(`[门户] ${new Date().toISOString()} TODO 统一虚拟背景尚未对接`);
                    }, type: 'button' },
                    React.createElement("span", null, M.SCHEDULE_VIRTUAL_BG),
                    React.createElement("span", { className: 'portal-schedule-nav-value' },
                        M.SCHEDULE_NOT_SET,
                        React.createElement(ChevronIcon, null))))) : null,
            localError || error ? (React.createElement("div", { className: 'portal-error inline' }, localError || error)) : null)),
        picker ? (React.createElement(SchedulePickerDrawer, { footer: picker === 'duration' ? (React.createElement("button", { className: 'portal-schedule-end-link', onClick: openEndPicker, type: 'button' }, M.SCHEDULE_PICK_END_TIME)) : null, onClose: () => setPicker(null), onConfirm: confirmPicker, title: pickerTitle }, picker === 'mute' ? (React.createElement("div", { className: 'portal-schedule-option-list' }, [
            SCHEDULE_MUTE.OFF,
            SCHEDULE_MUTE.ON,
            SCHEDULE_MUTE.AFTER_6
        ].map(mode => (React.createElement("button", { className: draftMuteMode === mode
                ? 'portal-schedule-option is-selected'
                : 'portal-schedule-option', key: mode, onClick: () => setDraftMuteMode(mode), type: 'button' },
            React.createElement("span", null, muteStatusText(mode)),
            draftMuteMode === mode ? React.createElement("span", { className: 'check' }, "\u2713") : null))))) : picker === 'duration' ? (React.createElement(React.Fragment, null,
            React.createElement(WheelColumn, { onChange: setDraftDurationHours, options: hourDurationOptions, selectedUnit: M.SCHEDULE_HOUR_LABEL, value: draftDurationHours }),
            React.createElement(WheelColumn, { onChange: setDraftDurationMinutes, options: minuteDurationOptions, selectedUnit: M.SCHEDULE_MINUTE_LABEL, value: draftDurationMinutes }))) : (React.createElement(React.Fragment, null,
            React.createElement(WheelColumn, { onChange: setDraftDate, options: dateOptions, value: draftDate }),
            React.createElement(WheelColumn, { onChange: setDraftHour, options: hourOptions, selectedUnit: M.SCHEDULE_HOUR_UNIT, value: draftHour }),
            React.createElement(WheelColumn, { onChange: setDraftMinute, options: minuteOptions, selectedUnit: M.SCHEDULE_MINUTE_UNIT, value: draftMinute }))))) : null));
}
